// Balance validation utility for GSEC transactions
// This utility validates that transaction amounts don't exceed available balance

/**
 * Validates if the transaction amount exceeds available balance
 * @param {string} isin - The ISIN to check
 * @param {number} transactionAmount - The amount being transacted
 * @param {string} transactionType - Type of transaction (Sell, Buy, Buyback, Repo, Reverse Repo)
 * @param {Array} gsecReportData - The GSEC report data containing available balances
 * @returns {Object} Validation result with success status and message
 */
export const validateAvailableBalance = (isin, transactionAmount, transactionType, gsecReportData) => {
  if (!isin || !transactionAmount || !gsecReportData) {
    return {
      isValid: false,
      message: 'Missing required data for balance validation'
    };
  }

  // Find ALL rows for this ISIN.
  const matchingRows = gsecReportData.filter(item => item.isin === isin);
  
  if (!matchingRows.length) {
    return {
      isValid: false,
      message: `ISIN ${isin} not found in GSEC report data`
    };
  }

  const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

  // IMPORTANT: The GSEC report repeats the SAME per-ISIN repo collateral on every
  // buy row for that ISIN, and pre-computes each row's available_balance as
  // (rowFaceValue - fullIsinCollateral). Summing per-row available_balance therefore
  // subtracts the collateral once PER ROW, i.e. N times for N rows, which can drive
  // the total wildly negative and produce phantom "insufficient balance" failures.
  //
  // Compute the available balance correctly at the ISIN level instead:
  //   available = (sum of remaining face across all buy rows) - (collateral once)
  const totalRemainingFace = matchingRows.reduce((sum, row) => sum + toNumber(row.face_value), 0);
  const repoCollateral = matchingRows.reduce(
    (max, row) => Math.max(max, toNumber(row.repo_collateral)),
    0
  );
  const availableBalance = Math.max(0, totalRemainingFace - repoCollateral);
  const requestedAmount = parseFloat(String(transactionAmount).replace(/,/g, '')) || 0;

  // For Sell, Buyback, and Repo (Sherwood borrows and delivers its own
  // securities as collateral) transactions, check available balance.
  if (transactionType === 'Sell' || transactionType === 'Buyback' || transactionType === 'Repo') {
    if (requestedAmount > availableBalance) {
      return {
        isValid: false,
        message: `Insufficient available balance. Available: ${availableBalance.toLocaleString()}, Requested: ${requestedAmount.toLocaleString()}`,
        availableBalance,
        requestedAmount,
        shortfall: requestedAmount - availableBalance
      };
    }
  }

  // For Buy and Reverse Repo (collateral comes from the counterparty)
  // transactions, no remaining-balance check is required
  if (transactionType === 'Buy' || transactionType === 'Reverse Repo') {
    return {
      isValid: true,
      message: '',
      availableBalance,
      requestedAmount,
      newBalance: availableBalance + requestedAmount
    };
  }

  return {
    isValid: true,
    message: `Transaction validated. Available balance: ${availableBalance.toLocaleString()}`,
    availableBalance,
    requestedAmount
  };
};

/**
 * Validates multiple deals for balance availability
 * @param {Array} selectedDeals - Array of selected deals with amounts
 * @param {string} transactionType - Type of transaction
 * @param {Array} gsecReportData - The GSEC report data
 * @returns {Object} Validation result for all deals
 */
export const validateMultipleDealsBalance = (selectedDeals, transactionType, gsecReportData) => {
  if (!selectedDeals || selectedDeals.length === 0) {
    return {
      isValid: true,
      message: 'No deals selected for validation'
    };
  }

  const validationResults = [];
  let totalRequestedAmount = 0;
  let hasErrors = false;

  // Group deals by ISIN to check total amount per ISIN
  const isinAmounts = {};
  
  selectedDeals.forEach(deal => {
    const amount = parseFloat(deal.amountToSell || deal.amount || 0);
    if (amount > 0) {
      // We need to get the ISIN from the deal data
      // This assumes the deal object has an isin property
      const isin = deal.isin || deal.deal?.isin;
      if (isin) {
        if (!isinAmounts[isin]) {
          isinAmounts[isin] = 0;
        }
        isinAmounts[isin] += amount;
        totalRequestedAmount += amount;
      }
    }
  });

  // Validate each ISIN
  Object.keys(isinAmounts).forEach(isin => {
    const validation = validateAvailableBalance(
      isin, 
      isinAmounts[isin], 
      transactionType, 
      gsecReportData
    );
    
    validationResults.push({
      isin,
      amount: isinAmounts[isin],
      ...validation
    });

    if (!validation.isValid) {
      hasErrors = true;
    }
  });

  return {
    isValid: !hasErrors,
    message: hasErrors ? 'Some deals exceed available balance' : 'All deals validated successfully',
    totalRequestedAmount,
    validationResults,
    hasErrors
  };
};

/**
 * Gets available balance for a specific ISIN
 * @param {string} isin - The ISIN to check
 * @param {Array} gsecReportData - The GSEC report data
 * @returns {number} Available balance for the ISIN
 */
export const getAvailableBalance = (isin, gsecReportData) => {
  if (!isin || !gsecReportData) return 0;

  const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;
  const matchingRows = gsecReportData.filter(item => item.isin === isin);
  if (!matchingRows.length) return 0;

  // Per-ISIN available = total remaining face across buy rows minus collateral (counted once).
  const totalRemainingFace = matchingRows.reduce((sum, row) => sum + toNumber(row.face_value), 0);
  const repoCollateral = matchingRows.reduce(
    (max, row) => Math.max(max, toNumber(row.repo_collateral)),
    0
  );
  return Math.max(0, totalRemainingFace - repoCollateral);
};

export default {
  validateAvailableBalance,
  validateMultipleDealsBalance,
  getAvailableBalance
};
